/**
 * Priority is contextual (list membership + rank), not a lifecycle state.
 * Use these helpers instead of lifecycle_state === 'priority'.
 */

import type { FocusList, Priority, Item } from '../types/prioritization';

/**
 * True iff the item is in the list's priority list (ordered ids).
 */
export function isInPriorityList(itemId: string, list: FocusList): boolean {
  return list.priorityIds.includes(itemId);
}

/**
 * 1-based rank in the list, or null if not in list.
 */
export function getPriorityRank(itemId: string, list: FocusList): number | null {
  const i = list.priorityIds.indexOf(itemId);
  return i === -1 ? null : i + 1;
}

/**
 * Full contextual priority status for an item in a list.
 */
export function getPriorityStatus(
  itemId: string,
  list: FocusList
): { isPriority: boolean; rank: number | null; listId: string } {
  const rank = getPriorityRank(itemId, list);
  return {
    isPriority: rank !== null,
    rank,
    listId: list.id,
  };
}

/**
 * Build Priority[] from a list's priorityIds and candidate lookup.
 * Items have lifecycle_state 'item'; priority is derived from list membership + rank.
 */
export function getPrioritiesFromList(
  list: FocusList,
  getItemById: (id: string) => Item | undefined
): Priority[] {
  return list.priorityIds
    .map((id, index) => {
      const c = getItemById(id);
      if (!c) return null;
      const rank = index + 1;
      const aboveTheLine = rank <= list.cutLinePosition;
      return {
        ...c,
        lifecycle_stage: 'ranking' as const,
        itemPoolId: list.id,
        rank,
        aboveTheLine,
        slottingRationale: (c as Priority).slottingRationale,
        scores: (c as Priority).scores,
      } as Priority;
    })
    .filter((p): p is Priority => p !== null);
}
